import { UserPlus, ArrowUpRight } from "lucide-react";

/**
 * Recent Sign-Ups widget — always visible above the Admin tabs.
 * Displays the six newest members with source attribution, so signups
 * are impossible to miss.
 */
export default function RecentSignups({ members = [], onViewAll }) {
  const recent = [...members]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  const todayCount = members.filter((m) => {
    const d = new Date(m.created_at);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  return (
    <section
      className="mt-12 border border-stone-200 bg-white"
      data-testid="admin-recent-signups"
    >
      <header className="flex items-center justify-between px-6 md:px-8 py-6 border-b border-stone-200">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 border border-stone-900 text-stone-900 flex items-center justify-center">
            <UserPlus className="h-4 w-4" />
          </div>
          <div>
            <div className="overline">§ Ledger</div>
            <div className="font-serif font-light text-xl leading-none mt-1">
              Recent Sign-Ups
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {todayCount > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-stone-900 text-stone-50 px-3 py-1.5">
              {todayCount} today
            </span>
          )}
          <button
            type="button"
            onClick={onViewAll}
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-900 hover:text-stone-500 inline-flex items-center gap-1 border-b border-stone-900 hover:border-stone-500 pb-0.5"
            data-testid="admin-recent-signups-view-all"
          >
            View all <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
      </header>

      {recent.length === 0 ? (
        <div className="px-8 py-16 text-center text-sm text-stone-500 font-light">
          No sign-ups yet. New members will appear here in real time.
        </div>
      ) : (
        <ul className="divide-y divide-stone-200" data-testid="admin-recent-signups-list">
          {recent.map((m, i) => {
            const joined = new Date(m.created_at);
            const label = joined.toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            return (
              <li
                key={m.user_id}
                className="px-6 md:px-8 py-5 flex items-center justify-between gap-6 hover:bg-stone-50 transition-colors"
                data-testid={`admin-signup-row-${m.user_id}`}
              >
                <div className="flex items-baseline gap-6 min-w-0 flex-1">
                  <span className="font-mono text-[10px] text-stone-400 tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-serif text-lg text-stone-900 truncate">
                        {m.name || "—"}
                      </span>
                      <span className="text-xs text-stone-500 truncate font-light">
                        {m.email}
                      </span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-500 mt-2 flex items-center gap-3">
                      <span>{(m.role_intent || "homeowner").replace(/_/g, " ")}</span>
                      {m.heard_about && (
                        <>
                          <span className="text-stone-300">·</span>
                          <span className="text-stone-700">
                            via {m.heard_about.replace(/_/g, " ")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-stone-500 whitespace-nowrap uppercase tracking-widest">
                  {label}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
